import type { HeroBest, HeroId } from '../content/heroes';
import type { PetsState } from '../content/pets';
import type { HeroSave, ItemSave, Visibility } from '../sim/types';

/** REST API shapes shared by client and server. */
export interface AccountStats {
  runs: number;
  kills: number;
  bestLevel: number;
  bossKills: number;
  revenantKills: number;
  duelWins: number;
  duelLosses: number;
  revenantsSlainOfMine: number;
  referrals: number;
}

export interface AccountView {
  id: string;
  name: string;
  avatar: number;
  fame: number;
  rating: number;
  isGuest: boolean;
  lang: string;
  createdAt: number;
  stats: AccountStats;
  guild: { id: string; name: string; tag: string; role: 'leader' | 'member'; code?: string } | null;
  titles: string[];
  favoriteHero: HeroId | null;
  pets: PetsState;
  /** living heroes kept between realms, one per slot */
  heroes: HeroSave[];
  heroSlots: number;
  /** highest rank ever reached per hero; unlocks the hero tree */
  heroBest: HeroBest;
  /** account vault: safe storage for items taken off heroes between realms */
  vault: ItemSave[];
  vaultSlots: number;
  visibility: Visibility;
  /** lets spectators spend fame on this account's heroes */
  showMode: boolean;
}

/** What a nickname lookup reveals, filtered by the target's visibility. */
export interface PlayerPresence {
  name: string;
  avatar: number;
  /** the target hides its whereabouts from this viewer */
  hidden: boolean;
  online: boolean;
  realm?: string;
  heroId?: HeroId;
  level?: number;
  rank?: number;
  showMode: boolean;
  watchers?: number;
  guild?: string;
}

export interface GuildMemberView {
  accountId: string;
  name: string;
  avatar: number;
  role: 'leader' | 'member';
  fame: number;
  online: boolean;
  joinedAt: number;
}

export interface GuildView {
  id: string;
  name: string;
  tag: string;
  code: string;
  points: number;
  createdAt: number;
  members: GuildMemberView[];
}

export interface LeaderboardEntry {
  rank: number;
  id: string;
  name: string;
  avatar?: number;
  value: number;
  extra?: string;
}

export type LeaderboardKind = 'fame' | 'bestLevel' | 'bossKills' | 'revenantKills' | 'rating' | 'guilds';

export interface NoticeView {
  id: number;
  key: string;
  vars: Record<string, string | number>;
  createdAt: number;
}

export interface AuthResponse {
  token: string;
  account: AccountView;
  /** google sign-in: the identity already had an account; progress was NOT merged */
  switched?: boolean;
  merged?: boolean;
}

export const NAME_RE = /^[\p{L}\p{N} _\-'.]{2,16}$/u;
export const AVATAR_COUNT = 12;
